import { GelatoRelay, type CallWithERC2771Request } from '@gelatonetwork/relay-sdk';
import { config } from './config';
import { ethers } from 'ethers6';

// Initialize Gelato Relay with Ink L2 specific configuration
const relay = new GelatoRelay({
  contract: {
    relayERC2771: config.gelato.trustedForwarder,
    relay1BalanceERC2771: config.gelato.trustedForwarder,
    relayERC2771zkSync: config.gelato.trustedForwarder,
    relayERC2771Abstract: config.gelato.trustedForwarder,
    relay1BalanceERC2771zkSync: config.gelato.trustedForwarder,
    relay1BalanceERC2771Abstract: config.gelato.trustedForwarder,
    relayConcurrentERC2771: config.gelato.trustedForwarder,
    relayConcurrentERC2771zkSync: config.gelato.trustedForwarder,
    relayConcurrentERC2771Abstract: config.gelato.trustedForwarder,
    relay1BalanceConcurrentERC2771: config.gelato.trustedForwarder,
    relay1BalanceConcurrentERC2771zkSync: config.gelato.trustedForwarder,
    relay1BalanceConcurrentERC2771Abstract: config.gelato.trustedForwarder,
  }
});

// Track which RPC is currently working
let currentRpcIndex = 0;

/**
 * Get an array of all available RPC URLs (primary + backups)
 * Priority: Gelato Pro (private) -> Free public RPCs
 * This ensures gasless transactions always use the most reliable RPC
 */
function getAllRpcUrls(): string[] {
  return [config.ink.primaryRpcUrl, ...config.ink.fallbackRpcUrls];
}

/**
 * Create a provider with automatic failover to backup RPCs
 * @param retries Number of RPC endpoints to try before failing
 * @returns ethers.JsonRpcProvider or null if all RPCs fail
 */
export async function createRobustProvider(retries: number = 3): Promise<ethers.JsonRpcProvider | null> {
  const rpcUrls = getAllRpcUrls();
  let attemptCount = 0;
  
  while (attemptCount < Math.min(retries, rpcUrls.length)) {
    const rpcUrl = rpcUrls[currentRpcIndex];
    
    try {
      console.log(`🔄 Attempting to connect to RPC [${currentRpcIndex + 1}/${rpcUrls.length}]: ${rpcUrl.substring(0, 50)}...`);
      
      const provider = new ethers.JsonRpcProvider(rpcUrl, {
        chainId: config.ink.chainId,
        name: 'Ink L2'
      });
      
      // Test the connection with a simple call (get block number with timeout)
      const blockNumberPromise = provider.getBlockNumber();
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('RPC timeout')), 5000)
      );
      
      await Promise.race([blockNumberPromise, timeoutPromise]);
      
      console.log(`✅ Successfully connected to RPC [${currentRpcIndex + 1}/${rpcUrls.length}]`);
      return provider;
      
    } catch (error: any) {
      console.warn(`⚠️ RPC [${currentRpcIndex + 1}/${rpcUrls.length}] failed:`, error.message);
      
      // Move to next RPC
      currentRpcIndex = (currentRpcIndex + 1) % rpcUrls.length;
      attemptCount++;
      
      // Wait a bit before trying next RPC
      if (attemptCount < Math.min(retries, rpcUrls.length)) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }
  
  console.error('❌ All RPC endpoints failed');
  return null;
}

/**
 * Execute a provider call with automatic retry on different RPCs
 * @param callFn Function that takes a provider and executes the call
 * @param maxRetries Maximum number of RPCs to try
 * @returns Result of the call
 */
export async function executeWithRpcFailover<T>(
  callFn: (provider: ethers.JsonRpcProvider) => Promise<T>,
  maxRetries: number = 3
): Promise<T> {
  const rpcUrls = getAllRpcUrls();
  let lastError: Error | null = null;
  
  for (let i = 0; i < Math.min(maxRetries, rpcUrls.length); i++) {
    const provider = await createRobustProvider(1);
    
    if (!provider) {
      lastError = new Error('Failed to create provider');
      continue;
    }
    
    try {
      const result = await callFn(provider);
      return result;
    } catch (error: any) {
      console.warn(`⚠️ RPC call failed on attempt ${i + 1}:`, error.message);
      lastError = error;
      
      // Move to next RPC for next attempt
      currentRpcIndex = (currentRpcIndex + 1) % rpcUrls.length;
      
      // Wait before retrying
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
  }
  
  throw lastError || new Error('All RPC attempts failed');
}

export interface GaslessDeploymentParams {
  name: string;
  symbol: string;
  npmAddress: string; // NPM address for DEX routing
  userAddress: string;
  provider: any; // ethers.BrowserProvider
  lpProvider?: 'relay' | 'velodrome'; // LP provider selection
}

export interface GaslessDeploymentResult {
  taskId: string;
  success: boolean;
  error?: string;
}

/**
 * Deploy a token using Gelato gasless transaction with RPC failover
 * @param params Token deployment parameters
 * @returns Task ID from Gelato
 */
export async function deployTokenGasless(
  params: GaslessDeploymentParams
): Promise<GaslessDeploymentResult> {
  try {
    const { name, symbol, npmAddress, userAddress, provider, lpProvider = 'relay' } = params;

    // Use V3 factory address (self-custody LP NFTs)
    const factoryAddress = '0xE01118dbcF8Ba2609d3ffBD6A53d9533c7526D60';

    // Encode the function call data for newMavrkToken(string,string,address)
    const functionSignature = 'newMavrkToken(string,string,address)';
    const abiCoder = new ethers.AbiCoder();
    
    // Create function selector (first 4 bytes of keccak256 hash)
    const functionSelector = ethers.id(functionSignature).slice(0, 10);
    
    // Encode parameters
    const encodedParams = abiCoder.encode(
      ['string', 'string', 'address'],
      [name, symbol, npmAddress]
    ).slice(2); // Remove '0x' prefix

    // Combine selector and params
    const data = functionSelector + encodedParams;

    console.log('🚀 Gelato Gasless Deployment (V2):', {
      name,
      symbol,
      npmAddress,
      userAddress,
      factoryAddress: factoryAddress,
      data,
    });

    // Prepare ERC-2771 request (sequential by default)
    const request: CallWithERC2771Request = {
      chainId: BigInt(config.ink.chainId),
      target: factoryAddress as `0x${string}`,
      data: data as `0x${string}`,
      user: userAddress as `0x${string}`,
      userDeadline: Math.floor(Date.now() / 1000) + 300, // 5 minutes deadline
    };

    console.log('📡 Sending ERC-2771 sponsored call to Gelato...', {
      chainId: request.chainId,
      target: request.target,
      user: request.user,
      userDeadline: request.userDeadline,
      dataLength: request.data.length,
      forwarder: config.gelato.trustedForwarder
    });

    // Debug: Check if we can call userNonce directly with RPC failover
    try {
      await executeWithRpcFailover(async (robustProvider) => {
        const functionSelector = ethers.id('userNonce(address)').slice(0, 10);
        const paddedAddress = userAddress.slice(2).padStart(64, '0');
        const calldata = functionSelector + paddedAddress;
        
        console.log('🔍 Debug userNonce call with robust provider:', {
          functionSelector,
          paddedAddress,
          calldata,
          forwarder: config.gelato.trustedForwarder
        });
        
        const userNonceCall = await robustProvider.call({
          to: config.gelato.trustedForwarder,
          data: calldata
        });
        console.log('🔍 Direct userNonce call result:', userNonceCall);
        
        // Also try calling the contract's name() function to verify it's working
        const nameCall = await robustProvider.call({
          to: config.gelato.trustedForwarder,
          data: ethers.id('name()').slice(0, 10)
        });
        console.log('🔍 Contract name call result:', nameCall);
        
        return true;
      });
    } catch (error) {
      console.log('⚠️ Direct contract calls failed on all RPCs:', error);
    }

    // Send the ERC-2771 sponsored call with provider and API key
    // Gelato SDK will use the user's wallet provider (MetaMask, etc.)
    // But we'll have our robust provider ready for receipt fetching
    const response = await relay.sponsoredCallERC2771(
      request,
      provider,
      config.gelato.apiKey
    );

    console.log('✅ Gelato response:', response);

    return {
      taskId: response.taskId,
      success: true,
    };
  } catch (error: any) {
    console.error('❌ Gelato deployment error:', error);
    
    // Provide more detailed error messages
    let errorMessage = error.message || 'Failed to deploy token via Gelato';
    
    if (error.message?.includes('timeout')) {
      errorMessage = 'RPC timeout - all backup RPCs failed. Please try again.';
    } else if (error.message?.includes('insufficient')) {
      errorMessage = 'Insufficient gas tank balance. Please contact support.';
    } else if (error.message?.includes('network')) {
      errorMessage = 'Network connection failed. Please check your connection and try again.';
    }
    
    return {
      taskId: '',
      success: false,
      error: errorMessage,
    };
  }
}

export interface GaslessDeploymentParamsEth {
  name: string;
  symbol: string;
  npmAddress: string; // Uniswap V3 NPM address
  baseTokenAddress: string; // Base token address (e.g., USD1)
  userAddress: string;
  provider: any; // ethers.BrowserProvider
}

/**
 * Deploy a token on Ethereum Mainnet using Gelato gasless transaction
 * Uses V4 factory with newMavrkToken(string,string,address,address) signature
 * @param params Token deployment parameters for Ethereum
 * @returns Task ID from Gelato
 */
export async function deployTokenGaslessEth(
  params: GaslessDeploymentParamsEth
): Promise<GaslessDeploymentResult> {
  try {
    const { name, symbol, npmAddress, baseTokenAddress, userAddress, provider } = params;

    // Use V4 factory address on Ethereum Mainnet
    const factoryAddress = config.ethereum.tokenFactoryAddress;

    // Encode the function call data for newMavrkToken(string,string,address,address)
    const functionSignature = 'newMavrkToken(string,string,address,address)';
    const abiCoder = new ethers.AbiCoder();
    
    // Create function selector (first 4 bytes of keccak256 hash)
    const functionSelector = ethers.id(functionSignature).slice(0, 10);
    
    // Encode parameters
    const encodedParams = abiCoder.encode(
      ['string', 'string', 'address', 'address'],
      [name, symbol, npmAddress, baseTokenAddress]
    ).slice(2); // Remove '0x' prefix

    // Combine selector and params
    const data = functionSelector + encodedParams;

    console.log('🚀 Gelato Gasless Deployment (Ethereum Mainnet):', {
      name,
      symbol,
      npmAddress,
      baseTokenAddress,
      userAddress,
      factoryAddress: factoryAddress,
      data,
    });

    // Initialize Gelato Relay for Ethereum Mainnet
    // Set all contract addresses to the Ethereum trustedForwarder for relay1BalanceERC2771
    const ethRelay = new GelatoRelay({
      contract: {
        relayERC2771: config.ethereum.trustedForwarder,
        relay1BalanceERC2771: config.ethereum.trustedForwarder,
        relayERC2771zkSync: config.ethereum.trustedForwarder,
        relayERC2771Abstract: config.ethereum.trustedForwarder,
        relay1BalanceERC2771zkSync: config.ethereum.trustedForwarder,
        relay1BalanceERC2771Abstract: config.ethereum.trustedForwarder,
        relayConcurrentERC2771: config.ethereum.trustedForwarder,
        relay1BalanceConcurrentERC2771: config.ethereum.trustedForwarder,
        relayConcurrentERC2771zkSync: config.ethereum.trustedForwarder,
        relayConcurrentERC2771Abstract: config.ethereum.trustedForwarder,
        relay1BalanceConcurrentERC2771zkSync: config.ethereum.trustedForwarder,
        relay1BalanceConcurrentERC2771Abstract: config.ethereum.trustedForwarder,
      },
    });

    // Prepare ERC-2771 request for Ethereum Mainnet
    // Note: Gelato SDK automatically estimates gas limit, we don't set it manually
    const request: CallWithERC2771Request = {
      chainId: BigInt(config.ethereum.chainId),
      target: factoryAddress as `0x${string}`,
      data: data as `0x${string}`,
      user: userAddress as `0x${string}`,
      userDeadline: Math.floor(Date.now() / 1000) + 300, // 5 minutes deadline
    };

    console.log('📡 Sending ERC-2771 sponsored call to Gelato (Ethereum Mainnet)...', {
      chainId: request.chainId,
      target: request.target,
      user: request.user,
      userDeadline: request.userDeadline,
      dataLength: request.data.length,
      forwarder: config.ethereum.trustedForwarder
    });

    // Send the ERC-2771 sponsored call
    const response = await ethRelay.sponsoredCallERC2771(
      request,
      provider,
      config.gelato.apiKey
    );

    console.log('✅ Gelato response (Ethereum):', response);

    return {
      taskId: response.taskId,
      success: true,
    };
  } catch (error: any) {
    console.error('❌ Gelato deployment error (Ethereum):', error);
    
    // Provide more detailed error messages
    let errorMessage = error.message || 'Failed to deploy token via Gelato on Ethereum';
    
    // Check for specific Gelato error patterns
    if (error.message?.includes('timeout')) {
      errorMessage = 'RPC timeout. Please try again.';
    } else if (error.message?.includes('insufficient') || error.message?.includes('balance')) {
      errorMessage = 'Insufficient USDC balance in Gelato gas tank. Please fund your 1Balance account at https://relay.gelato.network/';
    } else if (error.message?.includes('network')) {
      errorMessage = 'Network connection failed. Please check your connection and try again.';
    } else if (error.message?.includes('chain') || error.message?.includes('unsupported')) {
      errorMessage = 'Ethereum mainnet may not be enabled in your Gelato account. Please check your dashboard at https://relay.gelato.network/ and ensure chainId 1 (Ethereum) is enabled for sponsored transactions.';
    } else if (error.message?.includes('whitelist') || error.message?.includes('not allowed')) {
      errorMessage = 'Contract not whitelisted for Ethereum mainnet. Please add the factory contract to your Gelato whitelist in the dashboard.';
    } else if (error.message?.includes('api') || error.message?.includes('key')) {
      errorMessage = 'Invalid or unauthorized API key. Please verify your Gelato API key has Ethereum mainnet permissions.';
    }
    
    // Log full error for debugging
    console.error('Full Gelato error details:', {
      message: error.message,
      stack: error.stack,
      response: error.response || error.data,
      code: error.code,
    });
    
    return {
      taskId: '',
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Check the status of a Gelato task
 * @param taskId Task ID from Gelato
 * @returns Task status
 */
export async function getTaskStatus(taskId: string) {
  try {
    const response = await fetch(
      `https://relay.gelato.digital/tasks/status/${taskId}`
    );
    const data = await response.json();
    
    console.log('📊 Task status:', data);
    
    return {
      taskState: data.task?.taskState,
      transactionHash: data.task?.transactionHash,
      blockNumber: data.task?.blockNumber,
      executionDate: data.task?.executionDate,
    };
  } catch (error) {
    console.error('Error fetching task status:', error);
    return null;
  }
}

/**
 * Poll for task completion
 * @param taskId Task ID from Gelato
 * @param onUpdate Callback for status updates
 * @returns Final task result
 */
export async function waitForTaskCompletion(
  taskId: string,
  onUpdate?: (status: string) => void,
  maxAttempts: number = 120, // 120 attempts = 2 minutes max (increased from 60 seconds)
  interval: number = 1000 // Check every 1 second
): Promise<{
  success: boolean;
  transactionHash?: string;
  error?: string;
}> {
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      const status = await getTaskStatus(taskId);

      if (!status) {
        await new Promise((resolve) => setTimeout(resolve, interval));
        attempts++;
        continue;
      }

      const taskState = status.taskState;
      
      // Log detailed task status for debugging
      console.log('📊 Task status details:', {
        taskState,
        transactionHash: status.transactionHash,
        blockNumber: status.blockNumber,
        executionDate: status.executionDate,
        fullStatus: status
      });
      
      if (onUpdate) {
        onUpdate(taskState);
      }

      // Task completed successfully
      if (taskState === 'ExecSuccess' || taskState === 'CheckSuccess') {
        return {
          success: true,
          transactionHash: status.transactionHash,
        };
      }

      // Task failed
      if (taskState === 'ExecReverted' || taskState === 'Cancelled') {
        let errorMsg = '';
        if (taskState === 'Cancelled') {
          errorMsg = 'Transaction was cancelled by Gelato. This usually means:\n\n' +
            '⚠️ Ethereum Mainnet (chainId 1) may not be enabled in your Gelato 1Balance account.\n\n' +
            'To fix this:\n' +
            '1. Go to https://relay.gelato.network/\n' +
            '2. Navigate to your 1Balance account settings\n' +
            '3. Ensure Ethereum Mainnet (chainId 1) is enabled for sponsored transactions\n' +
            '4. Verify your factory contract (0x25CA68F2C3d6c26623670C93426d74C901068bD6) is whitelisted for Ethereum\n' +
            '5. Ensure you have sufficient USDC balance in your 1Balance account\n\n' +
            'Note: Gelato 1Balance uses USDC to pay for gas on Ethereum Mainnet.';
        } else {
          errorMsg = `Transaction reverted on-chain. Check the transaction hash for details.`;
        }
        return {
          success: false,
          error: errorMsg,
        };
      }

      // Still pending, wait and try again
      await new Promise((resolve) => setTimeout(resolve, interval));
      attempts++;
    } catch (error) {
      // Network error - log but continue polling
      console.warn(`⚠️ Error polling task status (attempt ${attempts + 1}/${maxAttempts}):`, error);
      
      // Only fail if we're near the end
      if (attempts >= maxAttempts - 5) {
        return {
          success: false,
          error: 'Task polling failed due to network issues',
        };
      }
      
      // Wait a bit longer before retrying after an error
      await new Promise((resolve) => setTimeout(resolve, interval * 2));
      attempts++;
    }
  }

  // Timeout
  return {
    success: false,
    error: 'Task timed out - check Gelato dashboard for status',
  };
}

/**
 * Get transaction receipt with RPC failover
 * This is crucial after Gelato executes the transaction
 * @param txHash Transaction hash
 * @param maxRetries Maximum number of RPCs to try
 * @returns Transaction receipt or null
 */
export async function getTransactionReceiptWithFailover(
  txHash: string,
  maxRetries: number = 3
): Promise<ethers.TransactionReceipt | null> {
  console.log(`🔍 Fetching transaction receipt for: ${txHash}`);
  
  try {
    return await executeWithRpcFailover(async (provider) => {
      // Add retry logic within each RPC attempt
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const receipt = await provider.getTransactionReceipt(txHash);
          
          if (receipt) {
            console.log(`✅ Receipt found on attempt ${attempt + 1}`);
            return receipt;
          }
          
          // Receipt not found yet, wait and retry
          console.log(`⏳ Receipt not found on attempt ${attempt + 1}, waiting...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          
        } catch (error: any) {
          console.warn(`⚠️ Error fetching receipt on attempt ${attempt + 1}:`, error.message);
          
          if (attempt === 2) {
            throw error; // Let the outer failover handle it
          }
          
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      throw new Error('Receipt not found after 3 attempts');
    }, maxRetries);
    
  } catch (error) {
    console.error('❌ Failed to fetch transaction receipt on all RPCs:', error);
    return null;
  }
}

/**
 * Get Gelato Gas Tank balance (requires API call)
 * Note: This is a placeholder - you'd need to implement the actual API call
 */
export async function getGasTankBalance(): Promise<number | null> {
  try {
    // This would require a backend endpoint or direct Gelato API call
    // For now, return null and handle in UI
    return null;
  } catch (error) {
    console.error('Error fetching gas tank balance:', error);
    return null;
  }
}

